<template>
  <div class="block-wrapper">
    <div>
      <el-tabs v-model="activeName" class="demo-tabs" @tab-click="() => {}">
        <el-tab-pane label="Background Image" name="backgroundImage">
          <div style="max-width: 200px">
            <ImageSelectorInput
              v-model="blockContent.content.backgroundImage.url"
              :multiple="false"
              :featuredImageSlug="
                blockContent.content.backgroundImage.url
                  ? blockContent.content.backgroundImage.url
                  : 'empty'
              "
              :returnSlug="true"
            ></ImageSelectorInput>
          </div>
        </el-tab-pane>
        <el-tab-pane label="Subtitle" name="subtitle">
          <p class="hint">Optional eyebrow above the headline (e.g. uppercase label).</p>
          <el-input
            v-model="blockContent.content.subtitle.text"
            placeholder="Subtitle"
          />
        </el-tab-pane>
        <el-tab-pane label="Headline" name="headline">
          <el-input
            v-model="blockContent.content.headline.text"
            placeholder="Please enter headline"
          />
        </el-tab-pane>
        <el-tab-pane label="Button" name="button">
          <div class="row">
            <div class="col">
              <el-select
                v-model="blockContent.content.button.type"
                class="mt-2"
                placeholder="Button type"
              >
                <el-option
                  v-for="item in options"
                  :key="item.value"
                  :label="item.label"
                  :value="item.value"
              /></el-select>
              <div class="mt-2">
                <el-select
                  v-if="blockContent.content.button.type == 'action'"
                  v-model="blockContent.content.button.link"
                  placeholder="Link type"
                >
                  <el-option
                    v-for="item in actionsList"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value"
                /></el-select>
              </div>
              <div
                class="mt-2"
                v-if="blockContent.content.button.type == 'internal-page'"
              >
                <el-select
                  v-model="blockContent.content.button.link"
                  filterable
                  remote
                  placeholder="Please enter a keyword"
                  :remote-method="remoteMethod"
                  :loading="loading"
                  value-key="id"
                >
                  <el-option
                    v-for="item in data"
                    :key="item.value"
                    :label="item.label"
                    :value="item"
                  />
                </el-select>
              </div>
            </div>
            <div class="col">
              <el-input
                v-model="blockContent.content.button.label"
                placeholder="Please enter button label"
              />
              <el-input
                class="mt-2"
                v-if="blockContent.content.button.type == 'link'"
                v-model="blockContent.content.button.link"
                placeholder="Please enter link"
              />
              <el-checkbox
                class="mt-2"
                v-if="blockContent.content.button.type != 'action'"
                v-model="blockContent.content.button.targetBlank"
                true-value="1"
                false-value="0"
                >Open in new tab?</el-checkbox
              >
            </div>
          </div>
        </el-tab-pane>
        <el-tab-pane label="Icons / features" name="icons">
          <p class="hint">
            Optional row of icons with labels (frontend: <code>icons</code> or legacy
            <code>features</code>).
          </p>
          <el-button size="small" class="mt-2" @click="addIcon">Add item</el-button>
          <div
            v-for="(icon, idx) in blockContent.content.icons"
            :key="'icon-' + idx"
            class="repeatable-row mt-2"
          >
            <p class="row-label">Icon {{ idx + 1 }}</p>
            <div style="max-width: 200px">
              <ImageSelectorInput
                v-model="icon.url"
                :multiple="false"
                :featuredImageSlug="icon.url ? icon.url : 'empty'"
                :returnSlug="true"
              />
            </div>
            <el-input
              v-model="icon.label"
              class="mt-2"
              placeholder="Label"
            />
            <el-button
              size="small"
              type="danger"
              class="mt-2"
              @click="removeIcon(idx)"
              >Remove</el-button
            >
          </div>
        </el-tab-pane>
        <el-tab-pane label="Hero slider" name="artworks">
          <p class="hint">
            Bottom artwork slides. Each slide: image, title, description (desktop),
            short description (mobile), optional CTA label + URL or path (stored as
            <code>artworks[]</code>; frontend also accepts
            <code>heroArtworks</code>).
          </p>
          <el-button size="small" class="mt-2" @click="addArtwork">Add slide</el-button>
          <div
            v-for="(art, idx) in blockContent.content.artworks"
            :key="'art-' + idx"
            class="repeatable-row mt-2"
          >
            <p class="row-label">Slide {{ idx + 1 }}</p>
            <div style="max-width: 200px">
              <ImageSelectorInput
                v-model="art.image.url"
                :multiple="false"
                :featuredImageSlug="
                  art.image.url ? art.image.url : 'empty'
                "
                :returnSlug="true"
              />
            </div>
            <el-input v-model="art.title" class="mt-2" placeholder="Title" />
            <el-input
              v-model="art.description"
              class="mt-2"
              type="textarea"
              :rows="4"
              placeholder="Description (desktop)"
            />
            <el-input
              v-model="art.shortDescription"
              class="mt-2"
              type="textarea"
              :rows="2"
              placeholder="Short description (mobile)"
            />
            <el-input
              v-model="art.linkLabel"
              class="mt-2"
              placeholder="CTA label (optional)"
            />
            <el-input
              v-model="art.href"
              class="mt-2"
              placeholder="CTA URL or path, e.g. /pages/foo or https://…"
            />
            <el-button
              size="small"
              type="danger"
              class="mt-2"
              @click="removeArtwork(idx)"
              >Remove slide</el-button
            >
          </div>
        </el-tab-pane>
      </el-tabs>
    </div>
  </div>
  <el-button
    @click="deleteBlock(blockData.id)"
    class="mt-2"
    size="small"
    type="danger"
    >Delete this block</el-button
  >
</template>

<script>
import ImageSelectorInput from "../../Tiptap/ImageSelectorInput.vue";

function emptyArtwork() {
  return {
    image: { url: "", type: "img" },
    title: "",
    description: "",
    shortDescription: "",
    linkLabel: "",
    href: "",
  };
}

export default {
  name: "HeroBlock",
  props: {
    blockData: Object,
  },
  data() {
    return {
      options: [
        {
          value: "link",
          label: "External Link",
        },
        {
          value: "action",
          label: "Action",
        },
        {
          value: "internal-page",
          label: "Internal page",
        },
      ],
      blockContent: {
        content: {
          backgroundImage: {
            url: "",
            type: "img",
          },
          headline: {
            text: "",
            type: "text",
          },
          subtitle: {
            text: "",
          },
          icons: [],
          artworks: [],
          button: {
            type: "",
            label: "",
            link: "",
            targetBlank: false,
          },
        },
      },
      activeName: "backgroundImage",
      actionsList: [
        {
          value: "focusScreen",
          label: "Focus Modal",
        },
      ],
      data: [],
      loading: false,
    };
  },
  created() {
    this.unsubscribe = this.$store.subscribe((mutation, state) => {
      if (mutation.type === "COLLECT_BLOCK_CONTENT") {
        this.$store.dispatch("updateBlockContent", {
          blockData: this.blockData,
          blockContent: this.blockContent,
        });
      }
    });
    if (this.blockData.created_at) {
      const c = this.blockData.content || {};
      if (c.button?.link?.id) {
        this.data = [c.button.link];
      }
      this.blockContent.content = this.mergeHeroContent(c);
    }
  },
  beforeUnmount() {
    this.unsubscribe();
  },
  components: {
    ImageSelectorInput,
  },
  watch: {
    "blockContent.content.button.type"(newV, oldV) {
      if (this.blockContent.content.button) {
        if (oldV) this.blockContent.content.button.link = "";
      }
    },
  },
  methods: {
    mergeHeroContent(c) {
      const iconSource = Array.isArray(c.icons)
        ? c.icons
        : Array.isArray(c.features)
        ? c.features
        : [];
      const artworkSource = Array.isArray(c.artworks)
        ? c.artworks
        : Array.isArray(c.heroArtworks)
        ? c.heroArtworks
        : [];

      return {
        backgroundImage: {
          url: "",
          type: "img",
          ...(c.backgroundImage || {}),
        },
        headline: { text: "", type: "text", ...(c.headline || {}) },
        subtitle: { text: "", ...(c.subtitle || {}) },
        icons: iconSource.map((item) => ({
          url: item.url || item.icon || "",
          label: item.label || item.text || "",
        })),
        artworks: artworkSource.map((a) => this.normalizeArtwork(a)),
        button: {
          type: "",
          label: "",
          link: "",
          targetBlank: false,
          ...(c.button || {}),
        },
      };
    },
    normalizeArtwork(a) {
      const base = emptyArtwork();
      return {
        ...base,
        image: { ...base.image, ...(a.image || {}) },
        title: a.title || "",
        description: a.description || "",
        shortDescription: a.shortDescription || "",
        linkLabel: a.linkLabel || "",
        href: a.href || "",
      };
    },
    addIcon() {
      this.blockContent.content.icons.push({ url: "", label: "" });
    },
    removeIcon(index) {
      this.blockContent.content.icons.splice(index, 1);
    },
    addArtwork() {
      this.blockContent.content.artworks.push(emptyArtwork());
    },
    removeArtwork(index) {
      this.blockContent.content.artworks.splice(index, 1);
    },
    deleteBlock(id) {
      this.$store.dispatch("removeBlockFromList", id);
    },
    remoteMethod: function (query) {
      if (query) {
        this.loading = true;
        this.data = [];
        this.$api
          .get(
            `${
              process.env.VUE_APP_SHARED_API
            }api/page-translations?search=${query.toLowerCase()}`
          )
          .then((res) => {
            if (res.data.data) {
              res.data.data.map((item) => {
                this.data.push({
                  id: item.id,
                  label: item.name || item.title,
                  value: item,
                });
              });
              this.loading = false;
            } else {
              this.loading = false;
            }
          });
      } else {
        this.data = [];
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.block-wrapper {
  border: #adb2ad solid 1px;
  padding: 16px 20px;
}
.mt-2 {
  margin-top: 20px;
}
.hint {
  font-size: 12px;
  color: #666;
  margin: 0;
}
.repeatable-row {
  border: 1px solid #e0e0e0;
  padding: 12px;
  border-radius: 4px;
}
.row-label {
  font-weight: 600;
  margin: 0 0 8px;
}
</style>
